import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Grid, Typography, Card, CardContent, CardActionArea, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Package, ArrowRight } from 'lucide-react';
import Logo from '../../components/common/Logo.tsx';

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'rgba(26, 26, 46, 0.1)',
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(8),
}));

const Header = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  zIndex: 100,
  background: 'rgba(26, 26, 46, 0.8)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
}));

const SolutionCard = styled(Card)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px rgba(138, 43, 226, 0.2)',
    border: '1px solid rgba(138, 43, 226, 0.4)',
  },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

const CardDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  marginBottom: theme.spacing(2),
}));

const MetricsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const MetricChip = styled(Chip)<{ metriccolor: string }>(({ theme, metriccolor }) => ({
  background: `${metriccolor}20`,
  color: metriccolor,
  fontWeight: 500,
  border: `1px solid ${metriccolor}40`,
}));

const ViewButton = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: '#8A2BE2',
  fontWeight: 500,
  marginTop: theme.spacing(2),
  '& svg': {
    marginLeft: theme.spacing(0.5),
    transition: 'transform 0.2s ease',
  },
  '&:hover svg': {
    transform: 'translateX(4px)',
  },
}));

// Mock solution data (simplified version of what's in SolutionDetail)
const solutions = [
  {
    id: '1',
    title: 'AI-Powered Customer Service Platform',
    description: 'Intelligent platform that handles routine inquiries automatically and provides agents with AI assistance for complex issues',
    purpose: 'Improve customer satisfaction while reducing support costs',
    topMetrics: [
      { name: 'Value', value: 9, color: '#FF4500' },
      { name: 'Velocity', value: 9, color: '#00CC99' },
    ],
  },
  {
    id: '2',
    title: 'Predictive Maintenance System',
    description: 'Create a system that predicts equipment failures before they occur using machine learning and IoT sensors',
    purpose: 'Reduce downtime and maintenance costs while extending equipment lifespan',
    topMetrics: [
      { name: 'Variety', value: 9, color: '#8A2BE2' },
      { name: 'Value', value: 9, color: '#FF4500' },
    ],
  },
];

const Solutions: React.FC = () => {
  return (
    <>
      <Header>
        <Logo size="medium" />
      </Header>

      <PageContainer>
        <Container maxWidth="lg">
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Package size={24} color="#8A2BE2" style={{ marginRight: 12 }} />
              <Typography variant="h4" sx={{ color: '#fff' }}>
                Solution Products
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Browse our solution products designed to solve specific business challenges
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {solutions.map((solution) => (
              <Grid item xs={12} md={6} key={solution.id}>
                <SolutionCard>
                  <CardActionArea component={Link} to={`/solution-detail/${solution.id}`}>
                    <CardContent sx={{ p: 3 }}>
                      <CardTitle variant="h6">{solution.title}</CardTitle>
                      <CardDescription variant="body2">{solution.description}</CardDescription>
                      
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                          Purpose:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {solution.purpose}
                        </Typography>
                      </Box>

                      <MetricsContainer>
                        {solution.topMetrics.map((metric) => (
                          <MetricChip
                            key={metric.name}
                            label={`${metric.name}: ${metric.value}/10`}
                            metriccolor={metric.color}
                            size="small"
                          />
                        ))}
                      </MetricsContainer>

                      <ViewButton>
                        View Solution Details <ArrowRight size={16} />
                      </ViewButton>
                    </CardContent>
                  </CardActionArea>
                </SolutionCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </PageContainer>
    </>
  );
};

export default Solutions; 